import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Input, Segmented, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../lib/slug";
import type { ChunkView } from "../shared/types";
import { ChunkCard } from "./chunk-card";
import { EditChunkDialog } from "./edit-chunk-dialog";
import { useChunks } from "./use-source-detail";

type View = "all" | "excluded";

interface ChunksPanelProps {
  sourceId: string;
  total: number;
  canEdit: boolean;
  playgroundHref: string | null;
}

function ChunkSkeletons() {
  return (
    <div className="space-y-3 p-5" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-3 rounded-lg border border-line p-4">
          <Skeleton className="h-4 w-[240px]" />
          <Skeleton className="h-12" />
          <Skeleton className="h-16" />
        </div>
      ))}
    </div>
  );
}

function EmptyChunks({ view, search, onClear }: { view: View; search: string; onClear: () => void }) {
  const { t } = useTranslation();
  const k = "deployKnowledge.sourceDetail.chunks";
  if (search) {
    return (
      <EmptyState
        icon="search"
        headingLevel={3}
        title={t(`${k}.noMatchTitle`)}
        description={t(`${k}.noMatchBody`)}
        action={<Button variant="secondary" onClick={onClear}>{t(`${k}.clearSearch`)}</Button>}
      />
    );
  }
  if (view === "excluded") return <EmptyState icon="eye-off" headingLevel={3} title={t(`${k}.noExcludedTitle`)} description={t(`${k}.noExcludedBody`)} />;
  return <EmptyState icon="layers" headingLevel={3} title={t(`${k}.emptyTitle`)} description={t(`${k}.emptyBody`)} />;
}

/** Chunk viewer: server search (`q`), All / Excluded (client filter of loaded pages), load more (offset). */
export function ChunksPanel({ sourceId, total, canEdit, playgroundHref }: ChunksPanelProps) {
  const { t } = useTranslation();
  const k = "deployKnowledge.sourceDetail.chunks";
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("all");
  const [editing, setEditing] = useState<ChunkView | null>(null);
  const q = useDebounced(search.trim());
  const chunks = useChunks(sourceId, q);
  const loaded = chunks.data?.pages.flat() ?? [];
  const visible = view === "excluded" ? loaded.filter((c) => c.is_excluded) : loaded;
  return (
    <div>
      <div className="flex flex-col gap-2 border-b border-line px-5 py-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input type="search" className="h-9 pl-9" placeholder={t(`${k}.search`)} aria-label={t(`${k}.search`)} value={search} onChange={(e) => { setSearch(e.target.value); }} />
        </div>
        <Segmented aria-label={t(`${k}.view`)} value={view} onValueChange={setView} options={[{ value: "all", label: t(`${k}.all`) }, { value: "excluded", label: t(`${k}.excludedView`) }]} />
      </div>
      {chunks.isPending ? (
        <ChunkSkeletons />
      ) : chunks.isError ? (
        <ErrorState headingLevel={3} title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void chunks.refetch()} retryLabel={t("common.retry")} />
      ) : visible.length === 0 && !chunks.hasNextPage ? (
        <EmptyChunks view={view} search={q} onClear={() => { setSearch(""); }} />
      ) : (
        <div className="space-y-3 p-5">
          {visible.map((chunk) => (
            <ChunkCard key={chunk.id} chunk={chunk} sourceId={sourceId} canEdit={canEdit} playgroundHref={playgroundHref} onEdit={setEditing} />
          ))}
        </div>
      )}
      {loaded.length > 0 ? (
        <div className="flex items-center justify-between border-t border-line px-5 py-3 text-xs text-fg-subtle">
          <span aria-live="polite">{t(`${k}.showing`, { shown: loaded.length, total: q ? loaded.length : Math.max(total, loaded.length) })}</span>
          {chunks.hasNextPage ? (
            <Button variant="secondary" size="sm" loading={chunks.isFetchingNextPage} onClick={() => void chunks.fetchNextPage()}>
              {t(`${k}.loadMore`)}
            </Button>
          ) : null}
        </div>
      ) : null}
      {editing ? (
        <EditChunkDialog
          chunk={editing}
          sourceId={sourceId}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}
